import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationBlocker } from './useNavigationBlocker';
import { Blocker, Transition, Location } from 'history';
import { ModalsContext } from '../providers/ModalsProvider';

export type CallbackPromptResult = {
  showPrompt: boolean;
  confirmNavigation: VoidFunction;
  cancelNavigation: VoidFunction;
};

export function useCallbackPrompt(modalId: string, callback?: VoidFunction): CallbackPromptResult {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [lastLocation, setLastLocation] = useState<Transition | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [modalsRef] = useContext(ModalsContext);

  const functionRefs = useRef({ navigate, callback });

  const cancelNavigation = () => {
    setShowPrompt(false);
    callback && callback();
  };

  const isDifferentLocation = (location: Location, nextLocation: Location): boolean => {
    if (location.pathname !== nextLocation.pathname) return true;
    if (location.search !== nextLocation.search) return true;
    return !!nextLocation.state;
  };

  // handle blocking when user click on another route prompt will be triggered
  const handleBlockedNavigation = useCallback<Blocker>(
    (nextLocation: Transition) => {
      // in if condition we are checking next location and current location are equals or not
      const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
      if (!confirmedNavigation && modalId === lastModalRef.id && isDifferentLocation(location, nextLocation.location)) {
        setShowPrompt(true);
        setLastLocation(nextLocation);
        return false;
      }
      return true;
    },
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
    [confirmedNavigation, modalId, modalsRef]
  );

  const confirmNavigation = useCallback(() => {
    setShowPrompt(false);
    setConfirmedNavigation(true);
  }, []);

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      const options = {
        ...(lastLocation.location.state ? { state: lastLocation.location.state } : {}),
        ...(lastLocation.location.search ? { search: lastLocation.location.search } : {})
      };
      functionRefs.current.navigate(lastLocation.location.pathname, options);
      functionRefs.current.callback && functionRefs.current.callback();
    }
  }, [confirmedNavigation, lastLocation]);

  useNavigationBlocker(handleBlockedNavigation);

  return { showPrompt, confirmNavigation, cancelNavigation };
}
